import { circle, ellipse, fill, flat, line, P, rect, shine, type IconDef, type Shape } from '../kit';

const r2 = (v: number): number => Math.round(v * 100) / 100;

/** How many numbers each path command takes. */
const ARGS: Record<string, number> = { m: 2, l: 2, h: 1, v: 1, c: 6, s: 4, q: 4, t: 2, a: 7, z: 0 };

/** One command's numbers, flipped left-to-right across x = 16. */
function flipArgs(cmd: string, g: number[]): string {
  const abs = cmd === cmd.toUpperCase();
  const fx = (x: number): number => r2(abs ? 32 - x : -x);
  const [a = 0, b = 0, c = 0, d = 0, e = 0, f = 0, h = 0] = g;
  switch (cmd.toLowerCase()) {
    case 'h':
      return `${cmd}${fx(a)}`;
    case 'v':
      return `${cmd}${a}`;
    case 'c':
      return `${cmd}${fx(a)} ${b} ${fx(c)} ${d} ${fx(e)} ${f}`;
    case 's':
    case 'q':
      return `${cmd}${fx(a)} ${b} ${fx(c)} ${d}`;
    case 'a':
      return `${cmd}${a} ${b} ${-c} ${d} ${1 - e} ${fx(f)} ${h}`;
    default:
      return `${cmd}${fx(a)} ${b}`;
  }
}

/** Path data flipped left-to-right across the middle of the box: draw one ear, get both. */
function mirror(d: string): string {
  let out = '';
  let cmd = 'M';
  let nums: number[] = [];
  for (const tok of d.match(/[a-zA-Z]|-?\d*\.?\d+/g) ?? []) {
    if (/[a-zA-Z]/.test(tok)) {
      cmd = tok;
      if (tok.toLowerCase() === 'z') out += tok;
      continue;
    }
    nums.push(Number(tok));
    if (nums.length === ARGS[cmd.toLowerCase()]) {
      out += flipArgs(cmd, nums);
      nums = [];
      if (cmd === 'M') cmd = 'L';
      if (cmd === 'm') cmd = 'l';
    }
  }
  return out;
}

/** The path and its mirror image, as one shape's data. */
const both = (d: string): string => d + mirror(d);

/** A fluffy ball: n round bumps around a circle (pom-poms); a bigger k makes shallower bumps. */
function puff(cx: number, cy: number, r: number, n: number, k = 1.08): string {
  const at = (i: number): string => {
    const a = ((i / n) * 2 - 0.5) * Math.PI;
    return `${r2(cx + r * Math.cos(a))} ${r2(cy + r * Math.sin(a))}`;
  };
  const b = r2(r * Math.sin(Math.PI / n) * k);
  let d = `M${at(0)}`;
  for (let i = 1; i <= n; i++) d += `A${b} ${b} 0 0 1 ${at(i)}`;
  return `${d}Z`;
}

/** A thick stroke with an ink edge (tails, legs, antlers): a wide ink line under a colored one. */
const tube = (d: string, color: string, w: number): Shape[] => [line(d, w + 1.6), line(d, w, color)];

/** Black eye dots with a white catchlight, like the dog's. */
const eyes = (r: number, ...xy: number[]): Shape[] => {
  let dots = '';
  let glints = '';
  for (let i = 0; i + 1 < xy.length; i += 2) {
    const x = xy[i] as number;
    const y = xy[i + 1] as number;
    dots += circle(x, y, r);
    glints += circle(x + r * 0.3, y - r * 0.36, Math.max(0.4, r * 0.32));
  }
  return [flat(P.black, dots), flat(P.white, glints)];
};

const GATOR =
  'M2.4 10.4Q4.95 11.98 4.55 13.67Q6.11 12.92 6.32 14.64Q7.56 13.41 8.31 14.99Q8.48 13.32 10.12 14.16Q10.5 12.36 12.01 13.42Q12.64 11.69 13.98 12.96Q14.87 11.34 16 12.8C16.2 10.4 17.6 8.8 19.4 8.8C21.2 8.8 22.4 10.2 22.4 12.2C24.6 12.3 25.8 12.5 26.4 12.8C26.8 11.8 28.4 11.8 28.6 13.2C29.2 13.6 29.6 14.2 29.6 15C29.7 16.2 29.4 17.6 28.4 18.4C26.2 19.6 22.6 20.2 20 21.6C16.8 23.6 12 23.8 9 22.4C5.6 20.6 3 16.4 2.4 10.4Z';
const CATFISH =
  'M5.2 16.4C5.2 13.4 7.4 11.6 10.8 11.2C15.4 10.6 20 12.2 24 14.6V17.6C20.2 20 15.6 21.6 11.4 21.4C7.6 21.2 5.2 19.4 5.2 16.4Z';
const CAT_BODY = '#9ca878';
const CAT_DARK = '#6f7f4e';
const BASS =
  'M8.8 14.2C11.6 11 16.4 9.6 21 10.2C24.4 10.6 26.8 11.6 28.8 12.6L23.8 16L28.4 19.8C25.8 21.8 20.8 22.4 16 21.2C12.8 20.4 10.4 19.2 8.8 17.8Z';
const COW_HEAD =
  'M16 6C20.6 6 23.4 7.8 23.4 11.6C23.4 15.2 22.6 18.4 21.4 20.6H10.6C9.4 18.4 8.6 15.2 8.6 11.6C8.6 7.8 11.4 6 16 6Z';
const WOLF =
  'M5.4 4.2C7.4 6.2 9.2 8.4 10.6 10.2C11.8 9.6 13.4 9.4 14.8 9.8L19.6 4.8L19.8 11.6C21.4 12.6 22.6 13.6 23.4 14.8L25.6 14.8L24.8 16.8L27 17.8L25.8 19.4C26.1 20.1 26.3 20.8 26.4 21.6L27.6 29.4L24.8 27.8L22.2 29.6L19.4 27.8L16.6 29.6L14 27.8L11.6 29.4C11.2 26.6 10.4 24.4 9.4 23L8.2 20.6L10 19.4L8.4 17.8C6.4 16 4.4 14 3.4 11.6C5.4 12.4 8 13.6 10.2 14.2C8.2 11.6 6.2 8.6 4.6 5.8C4.4 5 4.8 4.4 5.4 4.2Z';

export const animals = {
  /** Tank (or any good boy): a pit-mix, head on. */
  dog: [
    fill(P.tan, ellipse(16, 16.4, 9.6, 9)),
    fill(P.brown, 'M6.4 8.6C4 12.6 4.4 18 7 19.8C9 18.2 9.6 13.4 10.8 10.4C9.8 9 8 8.4 6.4 8.6Z'),
    fill(P.brown, 'M25.6 8.6C28 12.6 27.6 18 25 19.8C23 18.2 22.4 13.4 21.2 10.4C22.2 9 24 8.4 25.6 8.6Z'),
    fill(P.cream, ellipse(16, 21.2, 5.6, 4.2)),
    fill(P.pink, 'M14.8 23.2H17.2V25.6C17.2 26.4 16.6 27 16 27C15.4 27 14.8 26.4 14.8 25.6Z', { w: 1.1 }),
    flat(P.black, ellipse(16, 18.8, 2.3, 1.6)),
    line('M16 20.2V21.6M13.4 22.2C14.4 23.2 16 23 16 21.6C16 23 17.6 23.2 18.6 22.2', 1.2),
    flat(P.black, circle(12, 14.8, 1.4)),
    flat(P.black, circle(20, 14.8, 1.4)),
    flat(P.white, circle(12.4, 14.3, 0.45)),
    flat(P.white, circle(20.4, 14.3, 0.45)),
  ],
  /** A gator, side view: green with a pale belly, a long snout and a big toothy grin. */
  gator: [
    fill(P.green, 'M8.6 20V25C8.6 25.8 9.2 26.4 10 26.4H13.4C14.2 26.4 14.4 25.4 13.6 25L12.4 24.4V20Z'),
    fill(P.green, 'M17.6 20V25C17.6 25.8 18.2 26.4 19 26.4H22.4C23.2 26.4 23.4 25.4 22.6 25L21.4 24.4V20Z'),
    fill(P.green, GATOR),
    flat(
      P.lemon,
      'M20 21.6C16.8 23.6 12 23.8 9 22.4C7.98 21.86 7.03 21.1 6.19 20.15C9.6 20.6 15.4 19.8 20 21.6Z',
    ),
    line(GATOR, 1.6),
    fill(
      P.white,
      'M17.6 14.6C21.4 16.4 25.4 16.6 29.2 15.8C29.3 16.6 29 17.4 28.4 17.9C24.8 19 20.6 18.6 17.6 14.6Z',
      {
        w: 1.1,
      },
    ),
    line('M19.6 15.4L20.8 17.5L22.2 16L23.6 18.4L25 16.3L26.4 18.4L27.8 16.2', 1),
    ...eyes(1.35, 19.5, 11.4),
  ],
  /** A channel catfish, the one you noodle out of the crick: olive-gray, pale belly, long whiskers, forked tail. */
  catfish: [
    fill(
      CAT_DARK,
      'M22.8 15C24.8 12.4 27 10.4 29.6 9.2C29.2 11.8 28.4 14.2 27 16.2C28.4 18.2 29.2 20.6 29.6 23.2C27 22 24.8 20 22.8 17.4Z',
    ),
    fill(CAT_DARK, 'M11.6 11.4C12 9.6 12.6 8.2 13.4 7.2C14.8 8 16.2 9.6 17 11.2Z'),
    fill(CAT_DARK, 'M16.2 20.8C17 23 18.6 24 20.2 23.8C21.4 22.2 22.6 20 23.4 17.8Z'),
    fill(CAT_BODY, CATFISH),
    flat(
      CAT_DARK,
      'M6.73 12.83C7.7 11.95 9.1 11.4 10.8 11.2C15.4 10.6 20 12.2 24 14.6C19.4 13.6 12.4 13.8 6.73 12.83Z',
    ),
    flat(
      P.cream,
      'M24 17.6C20.2 20 15.6 21.6 11.4 21.4C9.12 21.28 7.34 20.58 6.29 19.4C10 19.4 18 18.6 24 16.2Z',
    ),
    line(CATFISH, 1.6),
    line('M12.4 12.6C13.6 14.6 13.6 17.8 12.2 19.8', 1.2),
    line('M5.8 17.4H8.2', 1.2),
    line(
      'M6.6 16.6C4.4 16 2.8 17 2.6 19.4C2.5 20.8 3.2 22 4.4 22.6M7.4 18.4C6.4 20 6.4 22.4 7.6 24.2M6.4 14.6C4.8 13.6 3.8 12.4 3.6 10.8',
      1.5,
    ),
    ...eyes(1.15, 9.6, 14.6),
  ],
  /** A Big Mouth Billy Bass: a green bass, mouth wide open, mounted on a wooden plaque. */
  'singing-bass': [
    fill(P.wood, rect(4.4, 6.6, 23.2, 19.8, 3)),
    fill(P.gold, rect(12.4, 22.4, 7.2, 2.4, 0.6), { w: 1.1 }),
    fill(P.darkRed, 'M29.2 12.2C29.8 14.6 29.8 17.6 28.8 20.2L23 16Z'),
    fill(
      P.green,
      'M9.6 14C7.6 11.6 5.4 10.2 2.8 9.8C3.8 12 4.2 14 4 16C4.2 18 3.8 20 2.8 22.2C5.4 21.8 7.6 20.4 9.6 18Z',
    ),
    fill(P.green, 'M11.4 12.2L12 8L13.6 9.6L15 7.2L16.4 9.2L18 7.4L19 9.6L20.4 8.4L21 11.6Z'),
    fill(P.green, BASS),
    flat(
      P.lemon,
      'M28.4 19.8C25.8 21.8 20.8 22.4 16 21.2C12.8 20.4 10.4 19.2 8.8 17.8C13 18.6 19.4 19.2 26.6 18.4Z',
    ),
    flat(
      P.darkGreen,
      'M12 15.4L14.6 14.4L17 15.4L19.4 14.4L21.4 15.2L21.2 17L19 16.4L16.8 17.4L14.4 16.4L12 17.2Z',
    ),
    line(BASS, 1.6),
    line('M20 12.4C21.2 14.6 21.2 17.6 20 19.4', 1.2),
    flat(P.lemon, circle(22.6, 13, 1.9)),
    ...eyes(1.15, 22.7, 13.1),
  ],
  /** A little orange goldfish, side view (the Kevin in the fish tank). */
  goldfish: [
    fill(
      P.amber,
      'M19.4 14.2C22 10.6 25.6 7.6 29.6 7.4C28.4 10.4 27.8 13.2 26.6 16C27.8 18.8 28.4 21.6 29.6 24.6C25.6 24.4 22 21.4 19.4 17.8Z',
    ),
    fill(P.amber, 'M9.6 10.4C10.4 7.2 13.4 5.6 17 6.2C16.4 7.8 16.6 9.6 17.6 11Z'),
    fill(P.amber, 'M12.4 21.6C12.6 24 14.6 25.4 16.6 25.4C16.2 24 16.6 22.6 17.6 21.4Z'),
    fill(P.orange, ellipse(12.6, 16, 8.4, 6.6)),
    line('M22.4 13.4L27 9.6M22.6 18.4L27 22.4', 1.2, P.orange),
    shine(ellipse(13.6, 11.8, 3.6, 1.2), 0.5),
    line('M12 11.6C13.4 13.8 13.4 17.6 11.8 19.8', 1.2),
    line('M5.4 17.6C6.4 18.4 7.6 18.4 8.4 17.6', 1.2),
    ...eyes(1.6, 8.4, 14.2),
  ],
  /** A little aquarium: blue water, gravel, a plant, and one orange fish. */
  'fish-tank': [
    fill(P.glass, rect(3.6, 7.6, 24.8, 18.8, 1)),
    flat(P.water, rect(4.4, 10.6, 23.2, 15)),
    flat(
      P.tan,
      'M4.4 25.6V22.8C6.4 21.8 8.4 22.8 10.4 22.2C12.4 21.6 14.6 22.8 16.6 22.2C18.8 21.6 21 22.8 23 22.2C24.8 21.6 26.4 22.2 27.6 22.6V25.6Z',
    ),
    flat(
      P.wood,
      circle(7, 24.2, 0.8) + circle(12.4, 24.4, 0.8) + circle(18.6, 24, 0.8) + circle(24.6, 24.4, 0.8),
    ),
    fill(P.green, 'M7.2 22.6C5.4 19.6 7.8 17.4 6.4 14.6C8.6 15.8 9.4 18.6 8.6 22.4Z', { w: 1.2 }),
    fill(P.green, 'M9 22.6C8.8 19.2 11 17.6 10.8 13.4C12.6 15.8 12.6 19.4 10.4 22.6Z', { w: 1.2 }),
    fill(
      P.orange,
      'M14.8 16.4C16.2 14.2 19.8 13.8 21.8 16C22.6 15.2 23.6 14.4 24.8 14.2C24.4 15.6 24.4 17.2 24.8 18.6C23.6 18.4 22.6 17.6 21.8 16.8C19.8 19 16.2 18.6 14.8 16.4Z',
      { w: 1.2 },
    ),
    flat(P.black, circle(16.8, 15.8, 0.8)),
    line(circle(13, 12.8, 0.9), 0.9, P.white),
    shine(rect(5.4, 11.6, 1.4, 6, 0.7), 0.6),
    fill(P.charcoal, rect(2.6, 5.6, 26.8, 3, 1)),
    fill(P.charcoal, rect(2.6, 25.4, 26.8, 3, 1)),
  ],
  /** A gray rat, side view: pointy snout, pink ears and feet, a long bare pink tail. */
  rat: [
    ...tube('M22.8 19.8C26.4 21.2 28.8 19.2 28.6 15.8C28.4 12.8 26.6 10.6 25 9.6', P.pink, 1.8),
    fill(
      P.gray,
      'M2.4 18.4C4 16.4 6.4 14.2 9.4 13.2C13.4 11.6 19 11.4 22 14.2C24.4 16.4 24.2 20.6 21.2 22C17.8 23.4 11.6 23.4 8.4 22.4C5.8 21.6 3.8 20.2 2.4 18.4Z',
    ),
    fill(P.pink, ellipse(8.8, 23, 1.8, 1.1) + ellipse(19, 23, 1.8, 1.1), { w: 1.1 }),
    fill(P.pink, circle(10.8, 12.8, 2.1)),
    flat(P.hotPink, circle(11, 13, 1), 0.45),
    fill(P.pink, circle(2.8, 18.3, 1), { w: 1.1 }),
    line('M4.8 18L2.4 16.6M5 19.2L2.6 20.4', 0.9),
    ...eyes(1.05, 7, 16.4),
  ],
  /** An opossum (Kevin): gray-white fur, white face, pink pointy nose, black-and-pink ears, long bare pink tail. */
  possum: [
    ...tube(
      'M23.6 18.6C27.6 18.6 29.6 21.2 28.8 24.2C28.2 26.6 25.4 27.4 24 25.8C23 24.6 23.6 23 25.2 23.2',
      P.pink,
      1.8,
    ),
    fill(P.black, rect(9.6, 18.4, 3, 7.2, 1.4) + rect(19, 18.4, 3, 7.2, 1.4)),
    fill(
      P.gray,
      'M9.6 13.6C11.4 10.6 14.6 9.2 18 9.4L19.2 8L20.2 9.8L21.8 9L22.2 10.8L24 10.6L24 12.6C26.2 14.2 26.6 17.6 25.2 20C23.8 22.4 20 23 15.6 22.8C12.4 22.6 10 21.4 9.4 19Z',
    ),
    line('M15.6 11.6L16.4 13.2M18.4 11.4L19 13M21 12L21.4 13.6', 1.2, P.white),
    fill(
      P.white,
      'M2.8 17.6C4.6 15.6 7.2 12.8 10.6 12C13.4 11.4 15.2 13.2 14.8 16C14.4 18.8 11.8 20.2 9 19.8C6.8 19.6 4.4 18.8 2.8 17.6Z',
    ),
    fill(P.black, ellipse(11.8, 11.4, 2.2, 2.4)),
    flat(P.pink, ellipse(11.6, 11.6, 1.1, 1.3)),
    fill(P.pink, ellipse(3.2, 17.4, 1.3, 1.1), { w: 1.1 }),
    ...eyes(1.1, 8.4, 15),
  ],
  /** A raccoon face: gray, the black bandit mask, striped (white-rimmed) ears. */
  raccoon: [
    fill(P.white, both('M6.8 11.6C6 8.6 6 5.8 7 3.8C7.6 3 8.6 3 9.4 3.6C11 4.8 12.6 6.4 13.6 8Z')),
    flat(P.darkGray, both('M8.2 9.4C7.8 7.8 7.8 6.2 8.4 5.2C9.6 5.8 10.8 7 11.6 8.2Z')),
    fill(
      P.gray,
      'M16 6.4C20.8 6.4 24.6 9.2 25.6 13.2L29.2 16.4L25.8 17.8L27.8 21.6L23.4 21.6C21.6 24.6 19 26.4 16 26.4C13 26.4 10.4 24.6 8.6 21.6L4.2 21.6L6.2 17.8L2.8 16.4L6.4 13.2C7.4 9.2 11.2 6.4 16 6.4Z',
    ),
    flat(
      P.white,
      'M16 10.8C19.8 10.4 23.2 11.6 24.4 13.8L22.2 17.6C21.8 20.6 19.4 23.8 16 23.8C12.6 23.8 10.2 20.6 9.8 17.6L7.6 13.8C8.8 11.6 12.2 10.4 16 10.8Z',
    ),
    flat(P.darkGray, 'M14.6 7.4H17.4L17 13H15Z'),
    flat(
      P.black,
      'M16 14C18.2 12.2 21.8 12 24.6 13.6L27 16.6C25.6 18.8 22.6 19.4 20 18.6C18.6 18.2 17.4 17.2 16 17.2C14.6 17.2 13.4 18.2 12 18.6C9.4 19.4 6.4 18.8 5 16.6L7.4 13.6C10.2 12 13.8 12.2 16 14Z',
    ),
    flat(P.white, circle(11.6, 15.6, 1.7) + circle(20.4, 15.6, 1.7)),
    ...eyes(1.05, 11.7, 15.7, 20.3, 15.7),
    flat(P.black, ellipse(16, 20, 1.9, 1.3)),
    line('M16 21.2V22.2M14 22.6C14.8 23.4 16 23.1 16 22.2C16 23.1 17.2 23.4 18 22.6', 1.2),
  ],
  /** A Holstein cow, head on: white with black patches, black ears, a big pink nose. */
  cow: [
    fill(P.cream, both('M11 8.2C9 7.6 7.6 5.8 7.8 3.4C9.2 4.6 11 5.4 12.8 5.8Z')),
    fill(P.black, both('M9.4 10.6C6.6 9.6 3.8 10.4 2.8 12.8C4.6 14.6 7.6 14.6 9.8 13.4Z')),
    flat(P.pink, both('M8.6 11.6C6.8 11.2 5.2 11.6 4.6 12.6C5.8 13.4 7.4 13.4 8.8 12.8Z')),
    fill(P.white, COW_HEAD),
    flat(
      P.black,
      'M17.6 6.2C21 6 23.4 7.8 23.4 11.4C23.4 13.8 23 16.2 22.2 18C20.4 17.6 18.6 15.6 18 13.4C17.4 11 18.6 9.2 17.6 6.2Z',
    ),
    flat(P.black, 'M9.2 9.2C10 7.6 11.6 7 12.6 7.8C13.2 9.2 12 10.6 10.4 10.8C9.6 10.9 9.2 10.2 9.2 9.2Z'),
    line(COW_HEAD, 1.6),
    flat(P.white, circle(19.6, 13.4, 1.9)),
    ...eyes(1.35, 12.4, 13.4, 19.6, 13.4),
    fill(P.pink, ellipse(16, 21.4, 7.4, 4.8)),
    flat('#d65d84', ellipse(13, 21.6, 1.3, 1.7) + ellipse(19, 21.6, 1.3, 1.7)),
  ],
  /** An angry rodeo bull, head on: big curved horns, mean brows, a nose ring, snorting. */
  bull: [
    fill(P.cream, both('M10.2 12.4C5.6 12.4 2.8 9.2 3.2 3.2C4.8 6.4 7.4 8.2 11 8.4Z')),
    fill(P.brown, both('M8.4 13C6.2 12.6 4.4 13.4 3.8 15C5.4 16.2 7.4 16.2 9 15.2Z')),
    fill(
      P.brown,
      'M16 6.8C20.4 6.8 23.2 8.6 23.4 12.4C23.6 15.8 22.8 18.6 21.6 20.6H10.4C9.2 18.6 8.4 15.8 8.6 12.4C8.8 8.6 11.6 6.8 16 6.8Z',
    ),
    fill(
      P.darkBrown,
      'M12.6 8C13 6.4 14.4 5.6 15.4 6.4C15.8 5.2 17.6 5 18.2 6.4C19.4 6.2 20 7.4 19.4 8.6C17.6 9.6 14.4 9.6 12.6 8Z',
    ),
    ...eyes(1.3, 12.8, 15, 19.2, 15),
    line(both('M10.6 11.6L14.4 13.4'), 1.8),
    fill(P.beige, ellipse(16, 21.6, 7, 4.8)),
    flat(P.darkBrown, ellipse(13, 21.4, 1.3, 1.7) + ellipse(19, 21.4, 1.3, 1.7)),
    ...tube(circle(16, 24.4, 2.2), P.gold, 1.3),
    fill(P.white, both('M4.2 27A1.6 1.6 0 0 1 4.6 23.8A2.2 2.2 0 0 1 8.8 23.4A1.6 1.6 0 0 1 8.6 27Z')),
  ],
  /** A white hen: red comb and wattle, yellow beak. */
  chicken: [
    fill(P.red, 'M7.8 7.2C6.8 4.8 8.2 2.8 10.2 3.6C10.8 2.2 13 2.2 13.6 3.8C15.4 3.8 16.2 5.8 14.8 7.4Z'),
    fill(P.yellow, 'M7 8L2.6 10.2L7.2 12.2Z'),
    ...tube('M14 24V28.4M12 28.4H16M19.6 24V28.4M17.6 28.4H21.6', P.orange, 1.3),
    fill(
      P.white,
      'M6.8 9.6C6.8 6.2 9 4.6 11.2 4.8C13.6 5 15 7 14.8 9.8C14.6 12.2 15.8 13.6 18.2 13.4C20.4 13.2 21.6 11.6 22.4 9.4C23 7.6 24.6 6.4 26.6 6.8C28.6 8.6 29.4 13.4 28.4 17.6C27 23 22.4 25.6 16.6 25.6C10.4 25.6 6.6 22 6.4 17C6.3 14.2 7 12 6.8 9.6Z',
    ),
    line('M24.4 9.6C25.4 11.4 25.8 13.6 25.4 15.6M22.6 12.8C23.6 14.2 24 15.8 23.8 17.4', 1.2),
    fill(
      P.cream,
      'M12.6 16.6C14.6 14.6 19.4 14.4 22 16.6C22.8 19 21.2 21.6 18.6 22C16.6 22.4 14.8 21.6 13.4 20.4C12.4 19.4 12 17.8 12.6 16.6Z',
      { w: 1.3 },
    ),
    fill(P.red, 'M7 11.4C5.8 12.4 5.6 14.4 6.8 14.8C8 15 8.4 13.2 8 11.6Z', { w: 1.2 }),
    ...eyes(1.2, 9.8, 8.4),
  ],
  /** A fancy pink-and-white poodle, side view, pom-pom haircut. */
  poodle: [
    ...tube('M23.6 15.6C25.6 13.6 26.6 11.6 26.8 9.6', P.white, 1.4),
    fill(P.pink, puff(27, 8.4, 2.1, 8, 1.5)),
    fill(P.white, rect(21.4, 18, 3, 8, 1.5)),
    fill(P.pink, puff(22.9, 26.6, 2.1, 8, 1.5)),
    fill(P.white, rect(10.4, 18, 3, 8, 1.5)),
    fill(P.pink, puff(11.9, 26.6, 2.1, 8, 1.5)),
    fill(P.white, rect(13, 14.2, 10.6, 5.2, 2.6)),
    fill(P.pink, puff(22.8, 16.4, 3.3, 10, 1.5)),
    fill(P.pink, puff(12.2, 15.8, 4.8, 13, 1.5)),
    fill(P.white, 'M11.4 8.2C9 8.4 6.6 9 5 10C3.6 10.8 3.6 12.6 5 13.2C6.8 14 9.4 14.2 11.8 13.6Z'),
    fill(P.pink, puff(10.6, 7, 3, 10, 1.5)),
    fill(P.pink, puff(12.2, 12.2, 2.5, 8, 1.5)),
    flat(P.black, circle(4.4, 11.5, 1.1)),
    ...eyes(1, 8.4, 10.7),
  ],
  /** A black cat sitting, yellow-green eyes, tail curled. */
  'black-cat': [
    ...tube('M20.8 26.8C25 26.8 27.4 24.6 27.2 21.2C27 18.4 25 17 23.4 17.8', P.charcoal, 2.6),
    fill(
      P.charcoal,
      'M14.4 13C18.6 13 21.6 17 22.4 21.6C23 25.2 22 28 19.8 28.4H9C6.8 28 5.8 25.2 6.4 21.6C7.2 17 10.2 13 14.4 13Z',
    ),
    line('M14.4 21.4V27.6', 1.2, P.darkGray),
    fill(P.charcoal, ellipse(11.6, 27.8, 2.4, 1.3) + ellipse(17.2, 27.8, 2.4, 1.3)),
    fill(
      P.charcoal,
      'M6.8 11.6C6.8 9.4 7.4 7.6 8.4 6.4L7.8 2.8L11.4 4.8C12.4 4.6 13.4 4.4 14.4 4.4C15.4 4.4 16.4 4.6 17.4 4.8L21 2.8L20.4 6.4C21.4 7.6 22 9.4 22 11.6C22 15.4 18.8 17.6 14.4 17.6C10 17.6 6.8 15.4 6.8 11.6Z',
    ),
    flat(P.pink, 'M9.1 6.4L9 4.8L10.8 5.7ZM19.7 6.4L19.8 4.8L18 5.7Z'),
    flat('#d4e84a', ellipse(11.2, 11, 1.9, 1.7) + ellipse(17.6, 11, 1.9, 1.7)),
    flat(P.black, ellipse(11.2, 11, 0.6, 1.4) + ellipse(17.6, 11, 0.6, 1.4)),
    flat(P.white, circle(11.9, 10.3, 0.45) + circle(18.3, 10.3, 0.45)),
    flat(P.pink, 'M13.4 13.6H15.4L14.4 14.8Z'),
    line('M10.4 14.4L6.4 13.6M10.4 15.4L6.6 16.2M18.4 14.4L22.4 13.6M18.4 15.4L22.2 16.2', 0.8, P.gray),
  ],
  /** A gray wolf, head tipped back, howling. */
  wolf: [
    fill(P.darkGray, 'M18.6 9.6L22.8 5.4L22.4 12.2Z'),
    fill(P.darkRed, 'M4.4 5.6L10.8 14.6L3 11.2C2.6 9.2 3 7.2 4.4 5.6Z'),
    fill(P.gray, WOLF),
    flat(
      P.white,
      'M10.2 14.2C12.6 17 13.6 21.4 14 27.8L11.6 29.4C11.2 26.6 10.4 24.4 9.4 23L8.2 20.6L10 19.4L8.4 17.8C6.4 16 4.4 14 3.4 11.6C5.4 12.4 8 13.6 10.2 14.2Z',
    ),
    flat(
      P.darkGray,
      'M19.8 11.6C21.4 12.6 22.6 13.6 23.4 14.8L25.6 14.8L24.8 16.8L27 17.8L25.8 19.4C26.1 20.1 26.3 20.8 26.4 21.6L27.6 29.4L24.8 27.8C24.4 22.4 22.6 16.6 18.8 13.2Z',
    ),
    line(WOLF, 1.6),
    flat(P.charcoal, 'M16.6 9.6L18.8 7L19 10.4Z'),
    flat(P.black, circle(5.2, 5, 1.4)),
    line('M11.8 12.6C12.4 11.8 13.6 11.6 14.4 12.2', 1.3),
  ],
  /** A deer head with antlers, head on, like it's mounted on the wall. */
  deer: [
    ...tube(
      both('M12.6 9.4C10 8.4 7.6 7 6.6 3.6M10.2 8.2C10.6 6.4 10.6 4.8 10.2 3.4M8.4 6.8C6.6 7.2 5 7 3.6 5.8'),
      P.beige,
      2,
    ),
    fill(P.wood, both('M10.4 11.6C7.2 9.6 4 10 2.6 12.2C4.4 14.8 7.6 15.6 10.6 14.4Z')),
    flat(P.tan, both('M9.6 12.2C7.6 11.2 5.4 11.4 4.4 12.4C5.6 13.6 7.6 14 9.6 13.4Z')),
    fill(
      P.wood,
      'M16 7.6C19.8 7.6 22.6 9.4 22.6 12.8C22.6 16.4 20.8 19.8 19.4 23.4C18.8 25.2 17.6 26.4 16 26.4C14.4 26.4 13.2 25.2 12.6 23.4C11.2 19.8 9.4 16.4 9.4 12.8C9.4 9.4 12.2 7.6 16 7.6Z',
    ),
    flat(P.cream, ellipse(16, 22.6, 3, 2.4)),
    fill(P.black, ellipse(16, 24.2, 2.1, 1.4), { w: 1.1 }),
    ...eyes(1.5, 12.6, 14.6, 19.4, 14.6),
  ],
  /** A green turtle, side view. */
  turtle: [
    fill(
      P.lime,
      'M20.6 18.6L22.6 24.4C22.8 25.2 23.6 25.6 24.4 25.4L26 25C26.8 24.8 27 24 26.6 23.4L24.4 18.6Z',
    ),
    fill(P.lime, 'M8.4 18.6L7.4 24.4C7.2 25.2 7.8 25.8 8.6 25.8H10.8C11.6 25.8 12 25.2 11.8 24.4L11.6 18.6Z'),
    fill(P.lime, 'M25.4 18.2L29 19.6L25.6 20.6Z'),
    fill(
      P.lime,
      'M10.8 18.2C10 16 8.8 15.4 6.8 15.6C4.2 15.8 2.6 14.4 2.6 12.4C2.6 10.2 4.4 8.8 6.6 9C9.2 9.2 10.6 11.2 11.4 13.4L13 15.4Z',
    ),
    fill(P.green, 'M8 18.6C8 11.4 12 6.8 17 6.8C22 6.8 26 11.4 26 18.6Z'),
    line('M12.6 17.6L14 12.2L17 9.6L20 12.2L21.4 17.6M14 12.2H20', 1.4, P.darkGreen),
    fill(P.lemon, rect(6.6, 17.4, 21, 3, 1.5)),
    ...eyes(1.1, 5.2, 11.6),
    line('M3.2 13.4C4 14 5 14.2 6 13.8', 1.1),
  ],
  /** A leopard face: golden, black rosette spots. */
  leopard: [
    fill(P.amber, circle(7.8, 8.2, 3.7) + circle(24.2, 8.2, 3.7)),
    flat(P.cream, circle(8, 8.4, 1.9) + circle(24, 8.4, 1.9)),
    fill(
      P.amber,
      'M16 5.6C22 5.6 26.4 9.1 26.4 14.9C26.4 17.9 27.4 19.7 28.3 21.4L25.3 21.6C23.6 25.3 20.1 27.4 16 27.4C11.9 27.4 8.4 25.3 6.7 21.6L3.7 21.4C4.6 19.7 5.6 17.9 5.6 14.9C5.6 9.1 10 5.6 16 5.6Z',
    ),
    flat(
      P.cream,
      'M16 19.2C17.3 18.2 20.1 18.2 21.2 19.9C22 21.6 20.8 23.6 18.8 23.8C17.7 24 16.7 23.6 16 22.9C15.3 23.6 14.3 24 13.2 23.8C11.2 23.6 10 21.6 10.8 19.9C11.9 18.2 14.7 18.2 16 19.2Z',
    ),
    line(circle(8.7, 16.4, 1.4) + circle(23.3, 16.4, 1.4) + circle(16, 9.1, 1.4), 1.3, P.black),
    flat(
      P.black,
      circle(11.9, 8.9, 1.1) +
        circle(20.1, 8.9, 1.1) +
        circle(9.1, 11.9, 1) +
        circle(22.9, 11.9, 1) +
        circle(8.2, 20.3, 1) +
        circle(23.8, 20.3, 1),
    ),
    ...eyes(1.5, 12.1, 14.3, 19.9, 14.3),
    fill(P.pink, 'M14.1 18.4H17.9C17.9 19.7 16.9 20.5 16 20.8C15.1 20.5 14.1 19.7 14.1 18.4Z', { w: 1.1 }),
    line('M16 20.8V22M14.1 22.7C14.9 23.3 16 23.1 16 22C16 23.1 17.1 23.3 17.9 22.7', 1.2),
  ],
  /** A bald eagle head in profile: white head, yellow hooked beak, brown neck. */
  eagle: [
    fill(P.darkBrown, 'M10.4 29.4C11.4 25.4 14 22.4 17.6 21.4L26 19.2C27.8 21.8 29 25.4 29.4 29.4Z'),
    fill(P.gold, 'M11 9.6C8 9.2 4.6 10 3.2 13C2.6 14.6 3 16.4 4.4 17.4C4.4 15.8 5.2 15 6.4 15.2L11.4 16.6Z'),
    fill(
      P.white,
      'M8.4 10.2C9.4 6.6 12.6 4.4 16.6 4.4C21.6 4.4 25.6 7.6 26.6 12.2L28.2 13.4L26.4 14.6L28.4 16.6L26 17.4L27.2 20.2L24 20.2L24.8 23L21.4 21.8L20.2 24.2L18.2 21.8C15 22.4 12 21 10.4 18.4Z',
    ),
    flat(P.yellow, circle(13.6, 11.2, 1.9)),
    ...eyes(1, 13.8, 11.3),
    line('M10.6 8.8L15.8 9.6', 1.6),
  ],
  /** A big purple inflatable car-lot gorilla, arms up, a bit wobbly. */
  'inflatable-gorilla': [
    ...tube('M10.4 15.6C7.8 13.4 6 10.2 5.8 6.4', P.purple, 4.4),
    ...tube('M21.6 15.6C25 15 26.8 12.8 26.2 9.6C26 8.4 26.4 7.2 27.2 6.4', P.purple, 4.4),
    fill(P.purple, circle(5.8, 5.4, 2.6) + circle(27.2, 5.8, 2.6)),
    fill(P.purple, rect(10.2, 22, 4.6, 6.8, 2.3) + rect(17.2, 22, 4.6, 6.8, 2.3)),
    fill(P.purple, ellipse(16, 20.4, 7.8, 6.8)),
    flat(P.lavender, ellipse(16, 21.6, 4.6, 4.4)),
    fill(P.purple, ellipse(16, 10.6, 5.8, 5.4)),
    fill(
      P.lavender,
      'M11.2 11.2C11.2 9 13.2 8.2 14.6 9.4C15.4 10 16.6 10 17.4 9.4C18.8 8.2 20.8 9 20.8 11.2C20.8 14.2 18.8 15.8 16 15.8C13.2 15.8 11.2 14.2 11.2 11.2Z',
      { w: 1.2 },
    ),
    ...eyes(1.05, 14, 11.2, 18, 11.2),
    flat(P.black, ellipse(15.2, 13.4, 0.6, 0.5) + ellipse(16.8, 13.4, 0.6, 0.5)),
    shine(ellipse(11.6, 17.8, 1.4, 2.6), 0.45),
    line('M2.6 13.4C3.4 14.6 3.4 16 2.6 17.2M29.4 13.4C28.6 14.6 28.6 16 29.4 17.2', 1.2),
  ],
  /** A pink plastic lawn flamingo on thin black wire legs. */
  flamingo: [
    line('M18.4 19.6L17.8 29.2M21 19.4L21.8 29.2', 1.3, P.black),
    fill(
      P.grass,
      'M14.6 29.4C15.2 27.8 16.2 26.8 17.4 26.6L18.2 24.8L19.4 26.6L20.8 25L21.6 26.8L23.6 25.8C23.8 27.2 23.4 28.6 22.6 29.4Z',
    ),
    ...tube('M10.6 6.6C12.8 8.8 12.6 11 10.6 12.4C9 13.6 9.8 15.6 12.6 16', P.hotPink, 2.4),
    fill(
      P.hotPink,
      'M11.4 16.2C11.4 13.4 14.2 11.8 17.8 12C21 12.2 24.8 11.4 28.4 9.4C28.4 13.2 26.8 16.8 23.6 18.4C20.4 20 15.6 20 13.4 19C12 18.4 11.4 17.4 11.4 16.2Z',
    ),
    fill(
      P.pink,
      'M15 14.6C17.6 13.2 22 13.2 25.4 13.8C24.4 16.6 21.4 18.4 18 18.4C16.2 18.4 14.8 17 15 14.6Z',
      { w: 1.2 },
    ),
    fill(P.white, 'M8.6 4.8C6.8 4.6 5 5.4 4.4 7.2C4.2 8 4.4 9 4.8 9.6C5.4 8.6 6.2 7.8 8.8 7.6Z', { w: 1.1 }),
    flat(
      P.black,
      'M4.6 6.8C4.3 7.6 4.4 8.8 4.8 9.6C5.3 8.9 5.8 8.3 6.4 7.9L5.6 5.9C5.2 6.1 4.8 6.4 4.6 6.8Z',
    ),
    fill(P.hotPink, circle(10.2, 5.4, 2.4)),
    ...eyes(0.9, 10.6, 5),
  ],
  /** A white concrete porch goose dressed up in a yellow raincoat and rain hat. */
  'porch-goose': [
    fill(P.orange, 'M8.6 27.6L7.6 29.6H13.2L12.4 27.6ZM15.8 27.6L15 29.6H20.6L19.8 27.6Z'),
    fill(P.white, 'M23.4 18.8L29.4 16.4C29.6 18.8 28.6 21.4 24.6 22.8Z'),
    ...tube('M14.8 16C14.4 13.2 12.4 11.8 12.6 8.6', P.white, 4),
    fill(
      P.yellow,
      'M12 14.6C9.6 16.2 8.2 19.2 7.6 22.6L6.8 26.6C6.6 27.4 7.2 28 8 28H24.2C25.2 28 25.8 27.2 25.6 26.4L24.6 22C24 18.6 22 15.6 19 14.6C16.8 13.8 14 13.8 12 14.6Z',
    ),
    shine(ellipse(20.6, 20.6, 1.2, 3.4), 0.45),
    line('M13.8 16.6C13.2 20.2 12.8 24 12.8 27.4', 1.2),
    flat(P.darkBrown, circle(11.4, 19.6, 0.8) + circle(11, 22.6, 0.8) + circle(10.8, 25.4, 0.8)),
    fill(P.yellow, 'M11.2 14.4L10.4 17.6L14.6 16.2L18.8 17.4L18.4 14.2C16.2 13.4 13.4 13.6 11.2 14.4Z', {
      w: 1.2,
    }),
    fill(P.orange, 'M9.2 7.6C7.2 7.6 4.8 8.2 3.6 9.6C5 10.6 7.4 10.8 9.4 10.2Z'),
    fill(P.white, ellipse(12.4, 8.2, 3.4, 3)),
    fill(P.yellow, 'M8.8 6.6C8.8 4 10.6 2.6 12.8 2.6C15 2.6 16.6 4.2 16.6 6.6Z'),
    fill(
      P.yellow,
      'M7.4 6.4C10.2 5.6 15.6 5.6 18.2 6.6C19.2 7.8 19 9.6 18 10.6C17 8.8 15 7.8 12.6 7.6C10.6 7.6 8.6 7.6 7.4 6.4Z',
    ),
    ...eyes(1, 11, 9),
  ],
} satisfies Record<string, IconDef>;
